import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { Badge } from '@/components/ui/badge'

export interface StackReview {
  id: string
  name: string
  /** taken_on */
  on: string
  /** questionId -> the choices ticked in that review. */
  choices: Map<string, string[]>
}

export interface StackQuestionRef {
  id: string
  sectionId: string
}

/**
 * How the team's stack has changed across every Tech Stack review.
 *
 * ## Why this is a timeline and not a chart
 *
 * A stack is a **set of names**, not a quantity. There is no score to plot, and
 * the temptation — counting the technologies and drawing the count over time —
 * would be actively misleading: dropping two tools and adopting one is usually
 * an improvement, and a line going down would read as decline. The page's own
 * header says as much, and this component is built to agree with it rather than
 * to sneak a number in.
 *
 * The dataviz guidance lands in the same place from the other direction: past
 * roughly seven classes that all carry meaning, the honest form is a table, not
 * more colours. Fourteen questions across four sections is well past that.
 *
 * So what gets visualised is the thing that actually has a shape — **change**.
 * Each review is one row on a spine; under it, what joined and what left since
 * the review before, grouped by section. The eye can run down the spine and see
 * where the churn was: a long entry is a quarter where the stack moved, a short
 * one is a quarter where it settled.
 *
 * Direction is carried by an icon and a word, never by colour alone: red/green
 * chips would put the single most important distinction on this page out of
 * reach for a colourblind reader.
 */
export function StackTimeline({
  reviews,
  questions,
  sectionName,
  className,
}: {
  /** Oldest first. Fewer than two and this renders nothing. */
  reviews: StackReview[]
  questions: StackQuestionRef[]
  sectionName: (sectionId: string) => string
  className?: string
}) {
  if (reviews.length < 2) return null

  const sectionOf = new Map(questions.map((q) => [q.id, q.sectionId]))

  // Newest first for display — the most recent change is the one being asked
  // about, and making someone scroll to the bottom for it is the wrong default.
  const steps = reviews
    .map((review, i) => {
      const before = i === 0 ? null : reviews[i - 1]
      if (!before) return null

      const joined: Array<{ section: string; value: string }> = []
      const left: Array<{ section: string; value: string }> = []

      for (const [questionId, section] of sectionOf) {
        const now = new Set(review.choices.get(questionId) ?? [])
        const then = new Set(before.choices.get(questionId) ?? [])
        // A question the later review never answered is silence, not removal.
        // Without this, an unfinished review reads as the team ripping out half
        // its stack in one quarter.
        if (!review.choices.has(questionId)) continue
        for (const v of now) if (!then.has(v)) joined.push({ section: sectionName(section), value: v })
        for (const v of then) if (!now.has(v)) left.push({ section: sectionName(section), value: v })
      }

      return { review, joined, left }
    })
    .filter((s): s is NonNullable<typeof s> => s !== null)
    .reverse()

  return (
    <ol className={cn('relative space-y-5', className)}>
      {steps.map(({ review, joined, left }) => (
        <li key={review.id} className="relative pl-6">
          {/* The spine. A hairline one shade off the surface, like every other
              piece of chrome in this app's charts. */}
          <span
            className="absolute left-[5px] top-4 h-full w-px bg-border last:hidden"
            aria-hidden="true"
          />
          <span
            className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-primary bg-card"
            aria-hidden="true"
          />

          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium">{review.name}</span>
            <span className="text-xs text-muted-foreground">{formatDate(review.on)}</span>
          </div>

          {joined.length === 0 && left.length === 0 ? (
            // Worth saying out loud. A gap here would read as missing data,
            // when "we reviewed and nothing changed" is a real and useful result.
            <p className="mt-1 text-xs text-muted-foreground">
              No changes from the review before — the stack held.
            </p>
          ) : (
            <div className="mt-1.5 space-y-1.5">
              <ChangeRow icon="joined" items={joined} />
              <ChangeRow icon="left" items={left} />
            </div>
          )}
        </li>
      ))}
    </ol>
  )
}

function ChangeRow({
  icon,
  items,
}: {
  icon: 'joined' | 'left'
  items: Array<{ section: string; value: string }>
}) {
  if (items.length === 0) return null
  const Icon = icon === 'joined' ? Plus : Minus
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-muted-foreground">
        <Icon className="h-3 w-3" aria-hidden="true" />
        {icon === 'joined' ? 'Added' : 'Dropped'}
      </span>
      {items.map((it) => (
        <Badge
          key={`${it.section}:${it.value}`}
          variant={icon === 'joined' ? 'secondary' : 'outline'}
          className={cn('text-[10px] font-normal', icon === 'left' && 'line-through opacity-70')}
          title={it.section}
        >
          {it.value}
        </Badge>
      ))}
    </div>
  )
}

/**
 * The stack as it stands, grouped by section — the answer to "what do we run
 * on", without scrolling fourteen question cards to assemble it in your head.
 */
export function StackNow({
  choices,
  questions,
  sections,
  className,
}: {
  choices: Map<string, string[]>
  questions: StackQuestionRef[]
  sections: Array<{ id: string; name: string }>
  className?: string
}) {
  const bySection = sections
    .map((s) => {
      const values = questions
        .filter((q) => q.sectionId === s.id)
        .flatMap((q) => choices.get(q.id) ?? [])
      // De-duplicated: two questions in a section can legitimately name the same
      // tool, and showing it twice looks like a bug.
      return { section: s, values: [...new Set(values)] }
    })
    .filter((g) => g.values.length > 0)

  if (bySection.length === 0) return null

  return (
    <dl className={cn('grid gap-x-6 gap-y-3 sm:grid-cols-2', className)}>
      {bySection.map(({ section, values }) => (
        <div key={section.id} className="min-w-0">
          <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {section.name}
          </dt>
          <dd className="mt-1 flex flex-wrap gap-1">
            {values.map((v) => (
              <Badge key={v} variant="secondary" className="text-[10px] font-normal">
                {v}
              </Badge>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  )
}
